import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import styles from "../../styles/pages/auth/UserLoginPage.module.css";
import PageSeo from "../../components/common/PageSeo";
import { useUserAuth } from "../../contexts/UserAuthContext";
import api from "../../utils/api/axiosInstance";
import { FaGoogle, FaExclamationCircle } from "react-icons/fa";
import {
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import useDebounce from "../../hooks/useDebounce";
import LoginShowcase from "../../components/auth/LoginShowcase";
import logo from "../../assets/images/logo/logo.svg";
import {
  getRememberedLoginId,
  isKeepLoggedInEnabled,
  persistRememberedLogin,
  setKeepLoggedInPreference,
} from "../../utils/loginRememberMe";

// Facebook / X kept for error-label handling if an old OAuth callback lands here.
// Login UI currently exposes Google only.
const OAUTH_PROVIDER_LABELS = {
  google: "Google",
  facebook: "Facebook",
  twitter: "X",
};

const UserLoginPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSignup, setIsSignup] = useState(false);
  const { login, refreshUser, user, userAuthChecked } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from
    ? `${location.state.from.pathname || "/community"}${location.state.from.search || ""}${location.state.from.hash || ""}`
    : "/community";
  const apiBaseUrl = (api.defaults.baseURL || "/api").replace(/\/$/, "");

  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [oauthPending, setOauthPending] = useState(
    () => searchParams.get("oauth") === "success",
  );
  // Hidden until the API confirms the Google strategy is configured.
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(api.get("/authuser/oauth/providers"))
      .then((res) => {
        if (!cancelled) setGoogleEnabled(res?.data?.google === true);
      })
      .catch(() => {
        if (!cancelled) setGoogleEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep me logged in (Facebook-style: longer session + optional username prefill)
  const [rememberMe, setRememberMe] = useState(() => isKeepLoggedInEnabled());

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    emailConfirm: "",
    password: "",
    passwordConfirm: "",
  });

  useEffect(() => {
    const mode = searchParams.get("mode");
    setIsSignup(mode === "signup");
  }, [searchParams]);

  // Finish the social-login round-trip from the backend OAuth callback.
  useEffect(() => {
    const oauthStatus = searchParams.get("oauth");
    if (!oauthStatus) return;

    const provider = searchParams.get("provider") || "social";
    const reason = searchParams.get("reason");
    const providerLabel = OAUTH_PROVIDER_LABELS[provider] || provider;

    const next = new URLSearchParams(searchParams);
    next.delete("oauth");
    next.delete("provider");
    next.delete("reason");
    setSearchParams(next, { replace: true });

    if (oauthStatus === "failed") {
      setOauthPending(false);
      const message =
        reason === "not_configured"
          ? `${providerLabel} login is not set up yet. Add OAuth credentials in the server environment.`
          : `${providerLabel} sign-in failed. Please try again.`;
      setLoginError(
        <span className={styles.loginErrorRow}>
          {message}
          <FaExclamationCircle className={styles.loginErrorIcon} />
        </span>,
      );
      return;
    }

    if (oauthStatus === "success") {
      setOauthPending(true);
      refreshUser?.().catch(() => {
        setOauthPending(false);
        setLoginError(
          <span className={styles.loginErrorRow}>
            Signed in with {providerLabel}, but we could not load your account.
            Please try again.
            <FaExclamationCircle className={styles.loginErrorIcon} />
          </span>,
        );
      });
    }
  }, [searchParams, setSearchParams, refreshUser]);

  const [formData, setFormData] = useState(() => ({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    emailconfirm: "",
    emailOrUsername: getRememberedLoginId(),
    password: "",
    passwordconfirm: "",
  }));

  // Prefill identifier when returning to login (e.g. after toggling from signup)
  useEffect(() => {
    if (!isSignup) {
      const saved = getRememberedLoginId();
      if (saved) {
        setFormData((f) => ({ ...f, emailOrUsername: saved }));
      }
    }
  }, [isSignup]);

  const handleChange = (e) => {
    if (loginError) setLoginError(null);
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleKeepLoggedInChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    setKeepLoggedInPreference(checked);
  };

  // Toggle between Login/Signup
  const handleToggle = () => {
    const newMode = isSignup ? "login" : "signup";
    setSearchParams({ mode: newMode });
    setLoginError(null);
    setShowPassword(false);
    setSubmitting(false);
    setFormData({
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      emailconfirm: "",
      emailOrUsername: newMode === "login" ? getRememberedLoginId() : "",
      password: "",
      passwordconfirm: "",
    });
    setErrors({
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      emailConfirm: "",
      password: "",
      passwordConfirm: "",
    });
  };

  const handleOAuthLogin = (provider) => {
    window.location.href = `${apiBaseUrl}/authuser/${provider}`;
  };

  // Debounced validations for signup
  const debouncedFirstName = useDebounce(formData.firstName);
  const debouncedLastName = useDebounce(formData.lastName);
  const debouncedUsername = useDebounce(formData.username);
  const debouncedEmail = useDebounce(formData.email);
  const debouncedEmailConfirm = useDebounce(formData.emailconfirm);
  const debouncedPassword = useDebounce(formData.password);
  const debouncedPasswordConfirm = useDebounce(formData.passwordconfirm);

  const handleErrors = useCallback(() => {
    const newErrors = {
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      emailConfirm: "",
      password: "",
      passwordConfirm: "",
    };

    // First name
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (/\s/.test(formData.firstName.trim())) {
      newErrors.firstName = "No internal spaces allowed";
    } else if (!/^[A-Za-z'-]{2,}$/.test(formData.firstName.trim())) {
      newErrors.firstName = "Use letters only (min 2)";
    }

    // Last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (/\s/.test(formData.lastName.trim())) {
      newErrors.lastName = "No internal spaces allowed";
    } else if (!/^[A-Za-z'-]{2,}$/.test(formData.lastName.trim())) {
      newErrors.lastName = "Use letters only (min 2)";
    }

    // Username (format only; uniqueness is enforced server-side)
    if (!formData.username) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = "Use only English letters, numbers, and underscore";
    }

    // Email (format only; uniqueness is enforced server-side)
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Confirm email
    if (formData.email !== formData.emailconfirm) {
      newErrors.emailConfirm = "Emails do not match";
    }

    // Password
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (
      !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.password)
    ) {
      newErrors.password =
        "Password must include uppercase, lowercase, number, and special char";
    }

    // Confirm password
    if (formData.password !== formData.passwordconfirm) {
      newErrors.passwordConfirm = "Passwords do not match";
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return !Object.values(newErrors).some(Boolean);
  }, [formData]);

  useEffect(() => {
    if (!isSignup) return;
    if (
      debouncedFirstName.length > 0 ||
      debouncedLastName.length > 0 ||
      debouncedUsername.length >= 3 ||
      debouncedEmail.includes("@") ||
      debouncedEmailConfirm.length > 0 ||
      debouncedPassword.length > 0 ||
      debouncedPasswordConfirm.length > 0
    ) {
      handleErrors();
    }
  }, [
    isSignup,
    debouncedFirstName,
    debouncedLastName,
    debouncedUsername,
    debouncedEmail,
    debouncedEmailConfirm,
    debouncedPassword,
    debouncedPasswordConfirm,
    handleErrors,
  ]);

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setLoginError(null);
    setSubmitting(true);

    try {
      if (isSignup) {
        const isValid = handleErrors();
        if (!isValid) {
          setSubmitting(false);
          return;
        }

        const payload = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          username: formData.username.trim(),
          email: formData.email.trim(),
          password: formData.password,
        };

        await api.post("/authuser/signup", payload);

        // After signup, log the user in using email + password
        const result = await login(formData.email, formData.password, rememberMe);
        if (result.success) {
          persistRememberedLogin(formData.email, rememberMe);
          navigate(redirectTo, { replace: true });
          return;
        }
        setLoginError(
          <span className={styles.loginErrorRow}>
            {result.error || "Account created, but login failed. Please log in."}
            <FaExclamationCircle className={styles.loginErrorIcon} />
          </span>
        );
      } else {
        const identifier = formData.emailOrUsername.trim();
        const result = await login(identifier, formData.password, rememberMe);
        if (result.success) {
          persistRememberedLogin(identifier, rememberMe);
          navigate(redirectTo, { replace: true });
          return;
        } else {
          // Show specific error messages for rate limiting and account status
          let errorMsg = result.error || "Invalid User name or password";
          if (result.isRateLimited && result.retryAfter) {
            errorMsg = `Too many login attempts. Please try again after ${result.retryAfter} minute(s).`;
          } else if (result.isSuspended) {
            errorMsg = result.error; // Use the suspension/ban message from server
          }
          
          setLoginError(
            <span className={styles.loginErrorRow}>
              {errorMsg}
              <FaExclamationCircle className={styles.loginErrorIcon} />
            </span>
          );
        }
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (isSignup ? "Signup failed. Please try again." : "Login failed. Please try again.");
      setLoginError(
        <span className={styles.loginErrorRow}>
          {msg}
          <FaExclamationCircle className={styles.loginErrorIcon} />
        </span>
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!userAuthChecked) {
    return (
      <div className={styles.pageShell}>
        <p className={styles.oauthPending} role="status">
          Checking your session…
        </p>
      </div>
    );
  }

  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className={styles.pageShell}>
      <PageSeo
        title={isSignup ? "Create Account" : "Log In"}
        description="Sign in to Simpary to compare products and connect with the community."
        pathname="/login"
        noIndex
      />
      <div className={styles.splitLayout}>
        <section className={styles.brandPanel}>
          <Link to="/" className={styles.logoLink}>
            <img src={logo} alt="Simpary" className={styles.logo} />
          </Link>

          <div className={styles.brandContent}>
            <h1 className={styles.tagline}>
              {isSignup ? (
                <>
                  Smart choices.
                  <br />
                  <span className={styles.taglineAccent}>Your community.</span>
                </>
              ) : (
                <>
                  Welcome back.
                  <br />
                  <span className={styles.taglineAccent}>Compare smarter.</span>
                </>
              )}
            </h1>

            <div className={styles.authBox}>
              <h2 className={styles.formTitle}>
                {isSignup ? "Create your account" : "Sign in"}
              </h2>
              {oauthPending && (
                <p className={styles.oauthPending} role="status">
                  Finishing social sign-in…
                </p>
              )}
              <form className="FieldForm" onSubmit={handleSubmit} autoComplete="on">
            {isSignup && (
              <div className={styles.nameFieldsGrid}>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First name"
                    aria-label="First name"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`${
                      errors.firstName
                        ? styles.inputError
                        : formData.firstName && !errors.firstName
                        ? styles.inputSuccess
                        : ""
                    }`}
                    required
                  />
                  {formData.firstName && (
                    <span className={styles.iconIndicator}>
                      {errors.firstName ? (
                        <FaTimesCircle color="#d32f2f" />
                      ) : (
                        <FaCheckCircle color="#2e7d32" />
                      )}
                    </span>
                  )}
                </div>

                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last name"
                    aria-label="Last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`${
                      errors.lastName
                        ? styles.inputError
                        : formData.lastName && !errors.lastName
                        ? styles.inputSuccess
                        : ""
                    }`}
                    required
                  />
                  {formData.lastName && (
                    <span className={styles.iconIndicator}>
                      {errors.lastName ? (
                        <FaTimesCircle color="#d32f2f" />
                      ) : (
                        <FaCheckCircle color="#2e7d32" />
                      )}
                    </span>
                  )}
                </div>
              </div>
            )}
            {isSignup && (
              <div className={styles.nameErrors}>
                {errors.firstName && (
                  <p className={styles.error}>{errors.firstName}</p>
                )}
                {errors.lastName && (
                  <p className={styles.error}>{errors.lastName}</p>
                )}
              </div>
            )}

            {isSignup && (
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  name="username"
                  placeholder="User name"
                  aria-label="User name"
                  value={formData.username}
                  onChange={handleChange}
                  className={`${
                    errors.username
                      ? styles.inputError
                      : formData.username && !errors.username
                      ? styles.inputSuccess
                      : ""
                  }`}
                  required
                />
                {formData.username && (
                  <span className={styles.iconIndicator}>
                    {errors.username ? (
                      <FaTimesCircle color="#d32f2f" />
                    ) : (
                      <FaCheckCircle color="#2e7d32" />
                    )}
                  </span>
                )}
              </div>
            )}
            {isSignup && errors.username && (
              <p className={styles.error}>{errors.username}</p>
            )}

            {isSignup && (
              <div className={styles.inputWrapper}>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  aria-label="Email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`${
                    errors.email
                      ? styles.inputError
                      : formData.email && !errors.email
                      ? styles.inputSuccess
                      : ""
                  }`}
                  required
                />
                {formData.email && (
                  <span className={styles.iconIndicator}>
                    {errors.email ? (
                      <FaTimesCircle color="#d32f2f" />
                    ) : (
                      <FaCheckCircle color="#2e7d32" />
                    )}
                  </span>
                )}
              </div>
            )}
            {isSignup && errors.email && (
              <p className={styles.error}>{errors.email}</p>
            )}

            {!isSignup && (
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  name="emailOrUsername"
                  placeholder="Email or Username"
                  aria-label="Email or Username"
                  value={formData.emailOrUsername}
                  onChange={handleChange}
                  autoComplete="username"
                  required
                />
              </div>
            )}

            {isSignup && (
              <div className={styles.inputWrapper}>
                <input
                  type="email"
                  name="emailconfirm"
                  placeholder="Confirm email"
                  aria-label="Confirm email"
                  value={formData.emailconfirm}
                  onChange={handleChange}
                  className={`${
                    errors.emailConfirm
                      ? styles.inputError
                      : formData.emailconfirm && !errors.emailConfirm
                      ? styles.inputSuccess
                      : ""
                  }`}
                  required
                />
                {formData.emailconfirm && (
                  <span className={styles.iconIndicator}>
                    {errors.emailConfirm ? (
                      <FaTimesCircle color="#d32f2f" />
                    ) : (
                      <FaCheckCircle color="#2e7d32" />
                    )}
                  </span>
                )}
              </div>
            )}
            {isSignup && errors.emailConfirm && (
              <p className={styles.error}>{errors.emailConfirm}</p>
            )}

            <div className={styles.inputWrapper}>
              <button
                type="button"
                className={styles.passwordToggle}
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((s) => !s)}
              >
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </button>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                aria-label="Password"
                value={formData.password}
                onChange={handleChange}
                autoComplete={isSignup ? "new-password" : "current-password"}
                required
              />
            </div>

            {loginError && <p className={styles.error}>{loginError}</p>}
            {isSignup && errors.password && (
              <p className={styles.error}>{errors.password}</p>
            )}

            {isSignup && (
              <div className={styles.inputWrapper}>
                <input
                  type="password"
                  name="passwordconfirm"
                  placeholder="Confirm password"
                  aria-label="Confirm password"
                  value={formData.passwordconfirm}
                  onChange={handleChange}
                  className={`${
                    errors.passwordConfirm
                      ? styles.inputError
                      : formData.passwordconfirm && !errors.passwordConfirm
                      ? styles.inputSuccess
                      : ""
                  }`}
                  required
                />
                {formData.passwordconfirm && (
                  <span className={styles.iconIndicator}>
                    {errors.passwordConfirm ? (
                      <FaTimesCircle color="#d32f2f" />
                    ) : (
                      <FaCheckCircle color="#2e7d32" />
                    )}
                  </span>
                )}
              </div>
            )}
            {isSignup && errors.passwordConfirm && (
              <p className={styles.error}>{errors.passwordConfirm}</p>
            )}

            {!isSignup && (
              <div className={styles.loginActionsRow}>
                <label className={styles.rememberMe} htmlFor="rememberMe">
                  Remember me
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleKeepLoggedInChange}
                  />
                </label>
                <Link to="/forgot-password" className={styles.forgotUser}>
                  Forgot Password?
                </Link>
              </div>
            )}

            <button
              type="submit"
              className={
                isSignup ? styles.submitBtnSignup : styles.submitBtnPrimary
              }
              disabled={submitting}
            >
              {submitting
                ? isSignup
                  ? "Creating..."
                  : "Signing in..."
                : isSignup
                  ? "Create Account"
                  : "Sign In"}
            </button>

            {googleEnabled && (
              <>
                <div className={styles.line}></div>
                <div className={styles.thirdPartyLogin}>
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin("google")}
                    aria-label="Continue with Google"
                  >
                    <FaGoogle
                      className={`${styles.icon} ${styles.google}`}
                      size={32}
                    />
                  </button>
                </div>
              </>
            )}
          </form>

              <div className={styles.modeSwitch}>
                <span className={styles.modeSwitchLabel}>
                  {isSignup
                    ? "Already have an account?"
                    : "Don't have an account?"}
                </span>
                <button
                  type="button"
                  className={styles.modeSwitchBtn}
                  onClick={handleToggle}
                >
                  {isSignup ? "Sign in" : "Create account"}
                </button>
              </div>
            </div>

            <p className={styles.legal}>
              By continuing, you agree to our{" "}
              <Link to="/terms">Terms of Service</Link> and{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </div>
        </section>

        <LoginShowcase />
      </div>
    </div>
  );
};

export default UserLoginPage;
